/* @flow */

import * as React from 'react';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import { useIntl, FormattedMessage } from 'react-intl';
import { getEventPlugins } from '@braindate/domain/lib/event/util';

import Modal from 'src/shared/app/base/modal/component/Modal';
import ChatIcon from 'src/shared/ui/component/icon/ChatIcon';

import BaseButton from 'src/shared/ui/component/button/BaseButton';
import messages from 'plugin/virtual-braindate/l10n/virtualBraindateL10n';

import { getBrowserInfo } from 'src/shared/core/util/browserUtil';

import styles from './BraindateFeedbackModal.style';
import useEvent from 'src/shared/domain/event/hook/useEvent';
import { customImpactSurvey } from 'src/shared/app/feature/settings/featureSettings';

import defaultFieldsData from './BraindateFeedbackModalDefaultFields';
import { useDispatch } from 'react-redux';
import { showExpressionOfGratitudePostBraindateModalBundle } from 'src/shared/app/expression-of-gratitude/action/expressionOfGratitudeAction';
import FormBuilder from 'src/shared/app/base/component/data-entry/form-builder/FormBuilder';
import { sendFeedback } from 'src/shared/domain/braindate/action/braindateActions';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import type { Braindate } from '@braindate/domain/lib/braindate/type';
import {
  dismissNotification,
  dismissNotificationsBundle,
} from 'src/shared/app/notification/action/uiNotificationActions';

const useStyles = createUseThemeStyles(styles);

type FieldsDataType = 'checkbox_list' | 'text' | 'checkbox';

type FieldsData = {
  key: string,
  body: Object,
  choices?: Array<FieldsData>,
  field_type: FieldsDataType,
  more?: Array<FieldsData>,
};

type Props = {
  notification: Notification<Braindate>,
  bundleNotifications?: Array<Notification<Braindate>>,
};

const BraindateFeedbackModal = ({
  notification,
  bundleNotifications,
}: Props): React.Node => {
  const { data: braindate } = notification;
  const classes = useStyles();
  const intl = useIntl();
  const event = useEvent();
  const dispatch = useDispatch();

  const customImpactPlugin = getEventPlugins(event)[customImpactSurvey];
  const fieldsData: Array<FieldsData> =
    customImpactPlugin &&
    customImpactPlugin.survey &&
    customImpactPlugin.survey.questions
      ? customImpactPlugin.survey.questions
      : defaultFieldsData;

  const dispatchExpressionBundle = () => {
    bundleNotifications &&
      dispatch(
        showExpressionOfGratitudePostBraindateModalBundle(
          bundleNotifications
            .map(({ data: bundleBraindate }) => bundleBraindate)
            .filter(Boolean),
        ),
      );
  };

  const handleSubmit = (values, { setSubmitting }) => {
    if (braindate) {
      dispatch(sendFeedback(braindate, getBrowserInfo(), values))
        .finally(() => {
          setSubmitting(false);
          bundleNotifications &&
            dispatch(dismissNotificationsBundle(bundleNotifications));
          dispatchExpressionBundle();
        })
        .catch((e) => {
          throw e;
        });
    }
  };
  const handleClose = () => {
    bundleNotifications &&
      dispatch(dismissNotificationsBundle(bundleNotifications));
    dispatch(dismissNotification(notification));
    dispatchExpressionBundle();
  };

  return (
    <Modal
      width="fit-content"
      title={intl.formatMessage(messages.feedbackHeader)}
      icon={<ChatIcon />}
      isOpen={notification.active}
      content={
        <div>
          <FormBuilder fieldsData={fieldsData} onSubmit={handleSubmit} />
          <p className={classes.actions}>
            <FormattedMessage
              {...messages.feedbackSkip}
              values={{
                a(text) {
                  return (
                    <BaseButton
                      className={classes.btnSkip}
                      onClick={() => handleClose()}
                    >
                      {text}
                    </BaseButton>
                  );
                },
              }}
            />
          </p>
        </div>
      }
      handleClose={handleClose}
      showClose
      hideActions
      shouldCloseOnOverlayClick={false}
    />
  );
};

export default React.memo<Props>(BraindateFeedbackModal);
